import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { get } from "helpers/api_helper"
import { usePortalTenant } from "context/PortalTenantContext"
import { getTenantBrandingFromStorage } from "utils/tenantBranding"
import {
  DEFAULT_ITEM_PREVIEW_THEME,
  ensurePreviewFontsLoaded,
  extractThemeFromTenantBranding,
  extractThemeFromWebsiteConfig,
} from "./itemPreviewTheme"

const resolveTenantId = authTenantId => {
  if (typeof localStorage === "undefined") return authTenantId || ""
  return (
    localStorage.getItem("TENANT_ID") ||
    sessionStorage.getItem("TENANT_ID") ||
    localStorage.getItem("tenantId") ||
    authTenantId ||
    ""
  )
}

/**
 * Loads published website theme tokens for the logged-in tenant.
 * Falls back to portal tenant branding (not the generic customer defaults)
 * when website config is missing.
 */
export const useItemPreviewTheme = (enabled = true) => {
  const authTenantId = useSelector(
    state =>
      state.Login?.authUser?.tenantId ||
      state.Login?.authUser?.user?.tenantId ||
      ""
  )
  const authBranding = useSelector(
    state => state.Login?.authUser?.tenantBranding || null
  )
  const { tenantBranding: portalTenantBranding } = usePortalTenant()

  const brandingFallback = useMemo(() => {
    return (
      extractThemeFromTenantBranding(authBranding) ||
      extractThemeFromTenantBranding(portalTenantBranding) ||
      extractThemeFromTenantBranding(getTenantBrandingFromStorage()) ||
      DEFAULT_ITEM_PREVIEW_THEME
    )
  }, [authBranding, portalTenantBranding])

  const [websiteTheme, setWebsiteTheme] = useState(null)
  const [loading, setLoading] = useState(Boolean(enabled))

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return undefined
    }

    let cancelled = false
    const tenantId = resolveTenantId(authTenantId)
    if (!tenantId) {
      setWebsiteTheme(null)
      setLoading(false)
      return undefined
    }

    setLoading(true)
    get(`public/website-config/${tenantId}`)
      .then(response => {
        if (cancelled) return
        const config = response?.data || response
        const fromWebsite = extractThemeFromWebsiteConfig(config)
        setWebsiteTheme(fromWebsite)
        if (fromWebsite) ensurePreviewFontsLoaded(fromWebsite)
        else ensurePreviewFontsLoaded(brandingFallback)
      })
      .catch(() => {
        if (!cancelled) {
          setWebsiteTheme(null)
          ensurePreviewFontsLoaded(brandingFallback)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, authTenantId, brandingFallback])

  const theme = websiteTheme || brandingFallback

  return { theme, loading }
}
